import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { ResponsiveContainer, ResponsiveHeader, ResponsiveContent, ResponsiveFooter } from '../ui/ResponsiveLayout';

interface MemorialDetails {
  musicPreferences: string;
  readings: string;
  speakers: string;
  flowers: string;
  donations: string;
  reception: boolean;
  receptionDetails: string;
  dress: string;
  personalTouches: string;
  finalInstructions: string;
}

const Frame222: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress, completeTask } = useSessionPersistence();

  const [details, setDetails] = useState<MemorialDetails>({
    musicPreferences: '',
    readings: '',
    speakers: '',
    flowers: '',
    donations: '',
    reception: false,
    receptionDetails: '',
    dress: '',
    personalTouches: '',
    finalInstructions: ''
  });

  // Helper function to save data directly to localStorage
  const saveToLocalStorage = (detailsToSave: MemorialDetails) => {
    try {
      const dataToSave = {
        answers: {
          memorialDetails: detailsToSave
        },
        lastUpdated: new Date().toISOString()
      };
      
      // Save to backup location for redundancy
      localStorage.setItem('memorial-details-backup', JSON.stringify(detailsToSave));
      localStorage.setItem('memorial-data-backup', JSON.stringify(dataToSave));
      
      return true;
    } catch (error) {
      console.error('[Frame222] Direct save failed:', error);
      return false;
    }
  };

  // Load saved data on component mount - OPTIMIZED FOR CROSS-DEVICE SYNC
  useEffect(() => {
    // Priority 1: Load from session (authoritative source for cross-device sync)
    if (session?.taskProgress?.funeral?.answers) {
      const savedData = session.taskProgress.funeral.answers.memorialDetails;
      if (savedData) {
        console.log('[Frame222] Loading from session:', savedData);
        setDetails(savedData);
        return; // Session data takes priority
      }
    }

    // Priority 2: Fall back to localStorage (for same-device persistence)
    try {
      const backupData = localStorage.getItem('memorial-details-backup');
      if (backupData) {
        const data = JSON.parse(backupData);
        if (data) {
          console.log('[Frame222] Loading from localStorage backup:', data);
          setDetails(data);
        }
      }
    } catch (error) {
      console.error('[Frame222] Failed to load from backup:', error);
    }
  }, [session?.taskProgress?.funeral]);

  // Auto-save functionality removed to prevent race conditions with immediate saves

  const hasMeaningfulData = (dets: MemorialDetails): boolean => {
    // Check if any meaningful fields are filled
    return !!(
      dets.musicPreferences.trim() ||
      dets.readings.trim() ||
      dets.speakers.trim() ||
      dets.flowers.trim() ||
      dets.donations.trim() ||
      dets.dress.trim() ||
      dets.personalTouches.trim() ||
      dets.finalInstructions.trim()
    );
  };

  const handleInputChange = (field: keyof MemorialDetails, value: string | boolean) => {
    const newDetails = {
      ...details,
      [field]: value
    };

    setDetails(newDetails);

    // IMMEDIATE DIRECT SAVE
    saveToLocalStorage(newDetails);

    // Also save to session asynchronously (non-blocking)
    setTimeout(() => {
      updateTaskProgress('funeral', {
        memorialDetails: newDetails
      }).then(() => {
        // Auto-complete if meaningful data exists
        if (hasMeaningfulData(newDetails)) {
          completeTask('funeral');
          console.log('[Frame222] Task marked complete - memorial details filled');
        }
      }).catch(error => {
        console.error('[Frame222] Session save failed:', error);
      });
    }, 0); // Execute after current event loop
  };

  const handleNext = () => {
    // IMMEDIATE DIRECT SAVE
    saveToLocalStorage(details);

    // Also save to session asynchronously
    setTimeout(() => {
      updateTaskProgress('funeral', {
        memorialDetails: details
      }).catch(error => {
        console.error('[Frame222] Session save failed on next:', error);
      });
    }, 0);

    navigate('/frame-223');
  };

  const handleBack = () => {
    // IMMEDIATE DIRECT SAVE
    saveToLocalStorage(details);

    // Also save to session asynchronously
    setTimeout(() => {
      updateTaskProgress('funeral', {
        memorialDetails: details
      }).catch(error => {
        console.error('[Frame222] Session save failed on back:', error);
      });
    }, 0);

    navigate('/frame-221');
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader
          title={`Your Memorial & Celebration Details`}
        />
        
        <div className="max-w-4xl">
          <div className="space-y-6">
            {/* Music Preferences */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-3">
                Music preferences
              </label>
              <textarea
                placeholder="List any specific songs, hymns, or types of music you'd like played during the service..."
                value={details.musicPreferences}
                onChange={(e) => handleInputChange('musicPreferences', e.target.value)}
                className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:border-transparent h-20 resize-none"
              />
            </div>

            {/* Readings */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-3">
                Readings or poems
              </label>
              <textarea
                placeholder="Specify any particular readings, poems, scriptures, or quotes you'd like included..."
                value={details.readings}
                onChange={(e) => handleInputChange('readings', e.target.value)}
                className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:border-transparent h-20 resize-none"
              />
            </div>

            {/* Speakers */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-3">
                Who would you like to speak at your service?
              </label>
              <textarea
                placeholder={`List specific people you would like to give eulogies or share memories (family members, friends, colleagues, clergy)...`}
                value={details.speakers}
                onChange={(e) => handleInputChange('speakers', e.target.value)}
                className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:border-transparent h-20 resize-none"
              />
            </div>

            {/* Flowers */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-3">
                Flower preferences
              </label>
              <textarea
                placeholder={`Any specific flower types, colors, or arrangements you would prefer? Or would you prefer donations instead of flowers?`}
                value={details.flowers}
                onChange={(e) => handleInputChange('flowers', e.target.value)}
                className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:border-transparent h-16 resize-none"
              />
            </div>

            {/* Charitable Donations */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-3">
                Charitable donations in lieu of flowers
              </label>
              <textarea
                placeholder={`If you prefer donations to charity instead of flowers, please specify which organizations...`}
                value={details.donations}
                onChange={(e) => handleInputChange('donations', e.target.value)}
                className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:border-transparent h-16 resize-none"
              />
            </div>

            {/* Reception */}
            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={details.reception}
                  onChange={(e) => handleInputChange('reception', e.target.checked)}
                  className="w-4 h-4 text-cyan-700 focus:ring-cyan-700 rounded"
                />
                <span className="text-stone-800 text-base font-medium font-sans">Include a reception or gathering after the service</span>
              </label>
              
              {details.reception && (
                <div className="mt-4">
                  <textarea
                    placeholder={`Describe your preferences for the reception (location, food, atmosphere, etc.)...`}
                    value={details.receptionDetails}
                    onChange={(e) => handleInputChange('receptionDetails', e.target.value)}
                    className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:border-transparent h-16 resize-none"
                  />
                </div>
              )}
            </div>

            {/* Dress Code */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-3">
                Dress code or attire preferences
              </label>
              <input
                type="text"
                placeholder="e.g., 'Bright colors to celebrate life', 'Traditional black', 'Casual attire'..."
                value={details.dress}
                onChange={(e) => handleInputChange('dress', e.target.value)}
                className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:border-transparent"
              />
            </div>

            {/* Personal Touches */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-3">
                Personal touches or special elements
              </label>
              <textarea
                placeholder={`Any special elements that reflect your personality or interests (photo displays, hobby displays, special decorations, etc.)...`}
                value={details.personalTouches}
                onChange={(e) => handleInputChange('personalTouches', e.target.value)}
                className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:border-transparent h-20 resize-none"
              />
            </div>

            {/* Final Instructions and Messages */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-3">
                Final instructions and messages
              </label>
              <textarea
                placeholder="Any final instructions, messages, or wishes you'd like to leave for your loved ones..."
                value={details.finalInstructions}
                onChange={(e) => handleInputChange('finalInstructions', e.target.value)}
                className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:border-transparent h-24 resize-none"
              />
            </div>
          </div>
        </div>
      </ResponsiveContent>
      
      <ResponsiveFooter
        title="Plan for Your Funeral and Memorial"
        subtitle="Add memorial and celebration details"
        progressPercent={66}
        onBack={handleBack}
        onNext={handleNext}
        nextText="Next"
        backText="Back"
      />
    </ResponsiveContainer>
  );
};

export default Frame222; 